import { Injectable, inject } from '@angular/core';
import type { DetalleAvancePlanNegocio, ProductorPlanNegocioItem } from '../../shared/services/negocio.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { GestionPlanAvanceStore } from '../../store/gestion-plan-avance.store';

const SESSION_KEY = 'plan_negocio_productor';

function clampPct(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Guarda el productor seleccionado al navegar a "Acciones".
 * Persiste en sessionStorage para recuperar el encabezado tras refresh (incluso al cambiar de sección).
 */
@Injectable({ providedIn: 'root' })
export class GestionAccionesStateService {
  private readonly planAvanceStore = inject(GestionPlanAvanceStore);

  private productor: ProductorPlanNegocioItem | null = null;
  private productor$ = new BehaviorSubject<ProductorPlanNegocioItem | null>(null);

  /** Señal para forzar la expansión del submenú lateral de Gestión (p. ej. al elegir acción del listado). */
  private expandirMenuGestion$ = new Subject<void>();

  /**
   * Avances (0..100) por pestaña + total. Se inicializan desde `detalle_avance` cuando viene
   * en el productor/listado, y se actualizan vía GET `/negocio/{id}/avance` tras guardar cada sección.
   */
  private avanceDiagnostico$ = new BehaviorSubject<number>(0);
  private avanceSoluciones$ = new BehaviorSubject<number>(0);
  private avancePlanTrabajo$ = new BehaviorSubject<number>(0);
  private avancePlanInversion$ = new BehaviorSubject<number>(0);
  private avanceTotal$ = new BehaviorSubject<number>(0);

  /**
   * ID PLAN NEGOCIO NUEVO
   * 
   */
  private accion$ = new BehaviorSubject<string | null>(null);

  accionObservable$ = this.accion$.asObservable();

  dispararAccion(idPlanNegocio: string | null) {
    this.accion$.next(idPlanNegocio);
  }

  /**
   * Flags para habilitar «Solicitar aprobación»: true si Guardar respondió OK con avance al 100% en esa sección,
   * o si al cargar la pestaña los datos ya vienen al 100%. No se actualizan al editar sin guardar.
   * Se reinician al cambiar productor/plan.
   */
  private diagnosticoPriorizacionCompleta$ = new BehaviorSubject<boolean>(false);
  private solucionesIdentificacionCompleta$ = new BehaviorSubject<boolean>(false);
  private planTrabajoCompleto$ = new BehaviorSubject<boolean>(false);
  private planInversionCompleto$ = new BehaviorSubject<boolean>(false);

  /** Avance total (0..100). */
  get avanceTotalActivo$() {
    return this.avanceTotal$.asObservable();
  }

  get avanceDiagnosticoActivo$() {
    return this.avanceDiagnostico$.asObservable();
  }

  get avanceSolucionesActivo$() {
    return this.avanceSoluciones$.asObservable();
  }

  get avancePlanTrabajoActivo$() {
    return this.avancePlanTrabajo$.asObservable();
  }

  get avancePlanInversionActivo$() {
    return this.avancePlanInversion$.asObservable();
  }

  /** Observable del productor actual (para que los tabs reaccionen cuando se carga tras refresh). */
  get productorActivo$() {
    return this.productor$.asObservable();
  }

  /** Observable para que el shell expanda el submenú de Gestión. */
  get solicitarExpandirMenuGestion$() {
    return this.expandirMenuGestion$.asObservable();
  }

  /** Solicita expandir el submenú lateral de Gestión. */
  expandirMenuGestion(): void {
    this.expandirMenuGestion$.next();
  }

  /** Diagnóstico: true si AVANCE PRIORIZACIÓN llegó a 100%. */
  get diagnosticoPriorizacionCompletaActivo$() {
    return this.diagnosticoPriorizacionCompleta$.asObservable();
  }

  /** Soluciones: true si AVANCE IDENTIFICACIÓN DE PROBLEMAS llegó a 100%. */
  get solucionesIdentificacionCompletaActivo$() {
    return this.solucionesIdentificacionCompleta$.asObservable();
  }

  /** Plan de trabajo: true si el avance por fechas está al 100% (o no hay ítems). */
  get planTrabajoCompletoActivo$() {
    return this.planTrabajoCompleto$.asObservable();
  }

  /** Plan de inversión: true si el avance del formulario está al 100% (o no hay ítems con inversión). */
  get planInversionCompletoActivo$() {
    return this.planInversionCompleto$.asObservable();
  }

  setProductor(item: ProductorPlanNegocioItem | null): void {
    this.productor = item;
    this.productor$.next(item);
    // Al cambiar de productor, reiniciar estados de avance para evitar arrastre entre productores.
    this.diagnosticoPriorizacionCompleta$.next(false);
    this.solucionesIdentificacionCompleta$.next(false);
    this.planTrabajoCompleto$.next(false);
    this.planInversionCompleto$.next(false);

    // Inicializar avances desde el backend (si existen) para poder calcular total al guardar una sola pestaña.
    const det = item?.detalle_avance ?? null;
    const diagnostico = clampPct(det?.diagnostico);
    const soluciones = clampPct(det?.soluciones);
    const planTrabajo = clampPct(det?.plan_de_trabajo);
    const planInversion = clampPct(det?.plan_de_inversion);
    this.avanceDiagnostico$.next(diagnostico);
    this.avanceSoluciones$.next(soluciones);
    this.avancePlanTrabajo$.next(planTrabajo);
    this.avancePlanInversion$.next(planInversion);
    // Total: preferir `detalle_avance.total` del API; si no viene, promedio de las 4 pestañas.
    const rawTotal = det?.total;
    const tieneTotalApi = det != null && rawTotal != null && Number.isFinite(Number(rawTotal));
    const total = tieneTotalApi
      ? clampPct(rawTotal)
      : this.calcularTotalDesdePartes(diagnostico, soluciones, planTrabajo, planInversion);
    this.avanceTotal$.next(total);
    this.planAvanceStore.sincronizarDesdeDetalleAvance(det ?? undefined);

    try {
      if (item) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(item));
      } else {
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      // sessionStorage puede fallar (privado, cuota)
    }
  }

  getProductor(): ProductorPlanNegocioItem | null {
    return this.productor;
  }

  /**
   * Actualiza el productor en memoria y sessionStorage cuando sigue siendo el mismo `id_productor`
   * (p. ej. tras guardar diagnóstico y cambiar estado del plan en backend).
   * No reinicia los flags de «completitud» por pestaña ni dispara el reset de `setProductor`.
   * @param detalleAvanceMerge Si se pasa, sustituye `item.detalle_avance` (p. ej. tras `GET .../avance` cuando el GET de productor no trae el detalle o viene desactualizado).
   */
  refrescarProductorActivo(
    item: ProductorPlanNegocioItem,
    detalleAvanceMerge?: DetalleAvancePlanNegocio | null
  ): void {
    if (!item?.id_productor?.trim() || this.productor?.id_productor !== item.id_productor) {
      return;
    }
    const merged: ProductorPlanNegocioItem =
      detalleAvanceMerge != null
        ? { ...item, detalle_avance: detalleAvanceMerge }
        : item;
    this.productor = merged;
    this.productor$.next(merged);

    const det = merged.detalle_avance ?? null;
    if (det) {
      const diagnostico = clampPct(det.diagnostico);
      const soluciones = clampPct(det.soluciones);
      const planTrabajo = clampPct(det.plan_de_trabajo);
      const planInversion = clampPct(det.plan_de_inversion);
      this.avanceDiagnostico$.next(diagnostico);
      this.avanceSoluciones$.next(soluciones);
      this.avancePlanTrabajo$.next(planTrabajo);
      this.avancePlanInversion$.next(planInversion);
      const rawTotal = det.total;
      const tieneTotalApi = rawTotal != null && Number.isFinite(Number(rawTotal));
      const total = tieneTotalApi
        ? clampPct(rawTotal)
        : this.calcularTotalDesdePartes(diagnostico, soluciones, planTrabajo, planInversion);
      this.avanceTotal$.next(total);
      this.planAvanceStore.sincronizarDesdeDetalleAvance(det);
    }

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(merged));
    } catch {
      // sessionStorage puede fallar (privado, cuota)
    }
  }

  /**
   * Calcula total como promedio simple de las 4 pestañas requeridas por Gestión Acciones.
   */
  private calcularTotalDesdePartes(
    diagnostico: number,
    soluciones: number,
    planTrabajo: number,
    planInversion: number
  ): number {
    return clampPct((diagnostico + soluciones + planTrabajo + planInversion) / 4);
  }

  /** Snapshots (sin observable) para consumo inmediato al guardar. */
  getAvanceDiagnostico(): number {
    return this.avanceDiagnostico$.value;
  }
  getAvanceSoluciones(): number {
    return this.avanceSoluciones$.value;
  }
  getAvancePlanTrabajo(): number {
    return this.avancePlanTrabajo$.value;
  }
  getAvancePlanInversion(): number {
    return this.avancePlanInversion$.value;
  }
  getAvanceTotal(): number {
    return this.avanceTotal$.value;
  }

  /**
   * Tras GET `/negocio/{id}/avance`: alinea porcentajes, total, Zustand y `productor.detalle_avance`.
   */
  aplicarDetalleAvanceDesdeGet(det: DetalleAvancePlanNegocio | null | undefined): void {
    if (det == null) return;
    const diagnostico = clampPct(det.diagnostico);
    const soluciones = clampPct(det.soluciones);
    const planTrabajo = clampPct(det.plan_de_trabajo);
    const planInversion = clampPct(det.plan_de_inversion);
    const margen = clampPct(det.margen_bruto);
    this.avanceDiagnostico$.next(diagnostico);
    this.avanceSoluciones$.next(soluciones);
    this.avancePlanTrabajo$.next(planTrabajo);
    this.avancePlanInversion$.next(planInversion);
    const rawTotal = det.total;
    const tieneTotalApi = rawTotal != null && Number.isFinite(Number(rawTotal));
    const total = tieneTotalApi
      ? clampPct(rawTotal)
      : this.calcularTotalDesdePartes(diagnostico, soluciones, planTrabajo, planInversion);
    this.avanceTotal$.next(total);
    const normalizado: DetalleAvancePlanNegocio = {
      total: tieneTotalApi ? clampPct(rawTotal) : total,
      diagnostico,
      soluciones,
      plan_de_trabajo: planTrabajo,
      plan_de_inversion: planInversion,
      margen_bruto: margen,
    };
    this.planAvanceStore.sincronizarDesdeDetalleAvance(normalizado);
    if (this.productor?.id_productor) {
      this.productor = { ...this.productor, detalle_avance: normalizado };
      this.productor$.next(this.productor);
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.productor));
      } catch {
        // sessionStorage puede fallar
      }
    }
  }

  private sincronizarZustandConTotalLocal(): void {
    this.planAvanceStore.setAvanceGeneral(this.getAvanceTotal());
  }

  /** Setters por pestaña: actualizan también total. */
  setAvanceDiagnostico(pct: number): void {
    const v = clampPct(pct);
    this.avanceDiagnostico$.next(v);
    this.avanceTotal$.next(
      this.calcularTotalDesdePartes(v, this.getAvanceSoluciones(), this.getAvancePlanTrabajo(), this.getAvancePlanInversion())
    );
    this.sincronizarZustandConTotalLocal();
  }

  setAvanceSoluciones(pct: number): void {
    const v = clampPct(pct);
    this.avanceSoluciones$.next(v);
    this.avanceTotal$.next(
      this.calcularTotalDesdePartes(this.getAvanceDiagnostico(), v, this.getAvancePlanTrabajo(), this.getAvancePlanInversion())
    );
    this.sincronizarZustandConTotalLocal();
  }

  setAvancePlanTrabajo(pct: number): void {
    const v = clampPct(pct);
    this.avancePlanTrabajo$.next(v);
    this.avanceTotal$.next(
      this.calcularTotalDesdePartes(this.getAvanceDiagnostico(), this.getAvanceSoluciones(), v, this.getAvancePlanInversion())
    );
    this.sincronizarZustandConTotalLocal();
  }

  setAvancePlanInversion(pct: number): void {
    const v = clampPct(pct);
    this.avancePlanInversion$.next(v);
    this.avanceTotal$.next(
      this.calcularTotalDesdePartes(this.getAvanceDiagnostico(), this.getAvanceSoluciones(), this.getAvancePlanTrabajo(), v)
    );
    this.sincronizarZustandConTotalLocal();
  }

  setDiagnosticoPriorizacionCompleta(value: boolean): void {
    this.diagnosticoPriorizacionCompleta$.next(Boolean(value));
  }

  setSolucionesIdentificacionCompleta(value: boolean): void {
    this.solucionesIdentificacionCompleta$.next(Boolean(value));
  }

  setPlanTrabajoCompleto(value: boolean): void {
    this.planTrabajoCompleto$.next(Boolean(value));
  }

  setPlanInversionCompleto(value: boolean): void {
    this.planInversionCompleto$.next(Boolean(value));
  }

  /**
   * Recupera el productor guardado en sessionStorage (útil tras refresh).
   * Solo devuelve si el id coincide con el de la URL.
   */
  getProductorFromSession(idProductor: string): ProductorPlanNegocioItem | null {
    if (!idProductor) return null;
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const item = JSON.parse(raw) as ProductorPlanNegocioItem;
      return item?.id_productor === idProductor ? item : null;
    } catch {
      return null;
    }
  }

  clear(): void {
    this.productor = null;
    this.productor$.next(null);
    this.diagnosticoPriorizacionCompleta$.next(false);
    this.solucionesIdentificacionCompleta$.next(false);
    this.planTrabajoCompleto$.next(false);
    this.planInversionCompleto$.next(false);
    this.avanceDiagnostico$.next(0);
    this.avanceSoluciones$.next(0);
    this.avancePlanTrabajo$.next(0);
    this.avancePlanInversion$.next(0);
    this.avanceTotal$.next(0);
    this.planAvanceStore.reset();
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {}
  }
}
